import * as THREE from 'three';
import {
  CameraManager,
  ORBIT_RADIANS_PER_PIXEL,
  dollyOrbitState,
  panOrbitState,
  rotateOrbitState,
} from './CameraManager';

/**
 * Landscape-design navigation (spec §7), on SketchUp's scheme.
 *
 * SketchUp is the tool this app's users navigate in all day, and half a scheme is worse than an
 * unfamiliar one: fingers that know where orbit lives will find pan there instead, and every view
 * change costs a moment of untangling. So the mapping is SketchUp's exactly —
 *
 * | Input | Effect |
 * | --- | --- |
 * | Middle-drag | Orbit |
 * | `Shift` + middle-drag | Pan |
 * | Wheel | Zoom toward the cursor |
 * | Left | Left alone, for the tools |
 *
 * — rather than the right-drag-orbits, middle-drag-pans arrangement the spec first wrote down.
 * `Shift` is read on every move, not just on the press, so it switches between orbit and pan
 * mid-drag the way SketchUp's does; you never have to let go and start again.
 *
 * Leaving the right button out of navigation is deliberate, not an oversight. In SketchUp it opens
 * a context menu, and holding one button free is what makes it possible to add one here later
 * without taking a navigation gesture back off the user.
 *
 * ## Saying which move is under way
 *
 * The scheme above is three moves on one button and a wheel, which is compact to hold in the hand
 * and invisible from outside it: nothing on screen distinguishes an orbit from a pan, and `Shift`
 * can swap one for the other without the mouse having moved. So the controller reports what it is
 * doing to {@link CameraControllerDeps.onNavAction}, and the HUD draws it on the pointer — which is
 * the one place already under the user's eye while the view is moving.
 *
 * The report is a fact about the camera, not about the input: the wheel says `zoom-in` only when the
 * distance actually changed, so scrolling against the near or far clamp says nothing rather than
 * promising a move that the clamp refused.
 *
 * Input mutates the {@link CameraManager} orbit state; the tested math lives in `CameraManager`.
 */
export interface CameraControllerDeps {
  /**
   * Told which camera move is under way, and told `null` when none is. Called only on a change, so
   * it is safe to hang a DOM write off directly.
   */
  onNavAction?: (action: NavAction | null) => void;
}

export class CameraController {
  private readonly element: HTMLElement;
  private readonly camera: CameraManager;
  private readonly deps: CameraControllerDeps;

  private gesture: NavGesture | null = null;
  /** The last move reported out, so a repeat is not reported twice. */
  private navAction: NavAction | null = null;
  /** Which pointer is driving the drag, so a second one touching down cannot steer it. */
  private pointerId: number | null = null;
  /** Takes the zoom pointer back down once the wheel has been still for a moment. */
  private zoomIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private lastX = 0;
  private lastY = 0;

  private readonly raycaster = new THREE.Raycaster();
  private readonly cursor = new THREE.Vector2();
  private readonly zoomPlane = new THREE.Plane();
  private readonly pivot = new THREE.Vector3();

  private readonly orbitSpeed = ORBIT_RADIANS_PER_PIXEL;
  private readonly zoomSpeed = 0.0015;

  private readonly onPointerDown = (e: PointerEvent): void => {
    const gesture = gestureFor(e.button, e.shiftKey);
    if (gesture === null) return;
    this.gesture = gesture;
    this.pointerId = e.pointerId;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    // A drag outranks the zoom pointer still lingering from the wheel a moment ago.
    this.clearZoomIdle();
    this.reportNav(gesture);
    this.element.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (this.gesture === null || e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;

    // Re-read the modifier every move rather than trusting the one held at the press: SketchUp lets
    // Shift borrow pan out of the middle of an orbit and hand it back on release, and a drag is
    // deltas all the way down, so switching costs nothing to resume from.
    this.gesture = modifiedGesture(e.shiftKey);
    this.reportNav(this.gesture);
    if (this.gesture === 'pan') {
      const perPixel = this.camera.worldUnitsPerPixel(this.viewportHeight());
      panOrbitState(this.camera.orbit, dx, dy, perPixel, this.camera.screenBasis());
    } else {
      rotateOrbitState(this.camera.orbit, dx, dy, this.orbitSpeed);
    }
    this.camera.applyOrbit();
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (this.gesture === null || e.pointerId !== this.pointerId) return;
    this.gesture = null;
    this.pointerId = null;
    this.reportNav(null);
    if (this.element.hasPointerCapture(e.pointerId)) {
      this.element.releasePointerCapture(e.pointerId);
    }
  };

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const pixels = wheelPixels(e);
    const factor = Math.exp(pixels * this.zoomSpeed);
    const applied = dollyOrbitState(
      this.camera.orbit,
      factor,
      this.camera.clamps,
      this.zoomPivot(e) ?? undefined,
    );
    this.camera.applyOrbit();
    // A drag keeps the pointer for its whole length — SketchUp lets you zoom in the middle of an
    // orbit, and the orbit is still the thing the hand is doing. `applied === 1` is the clamp
    // refusing the notch, and a pointer that says "zooming" while nothing moves is a lie.
    if (this.gesture !== null || applied === 1) return;
    this.reportNav(zoomAction(pixels));
    this.armZoomIdle();
  };

  /**
   * Switch back to the tool's own pointer once the wheel has been quiet for a moment.
   *
   * A wheel has no release to listen for, so the zoom pointer has to time itself out. The delay is
   * long enough to ride through the gaps inside one flick of a notchy wheel, and short enough that
   * it is gone before the hand has moved on.
   */
  private armZoomIdle(): void {
    this.clearZoomIdle();
    this.zoomIdleTimer = setTimeout(() => {
      this.zoomIdleTimer = null;
      this.reportNav(null);
    }, ZOOM_POINTER_LINGER_MS);
  }

  private clearZoomIdle(): void {
    if (this.zoomIdleTimer !== null) clearTimeout(this.zoomIdleTimer);
    this.zoomIdleTimer = null;
  }

  /** Pass a change of camera move on to the HUD. Repeats are swallowed here, not by the listener. */
  private reportNav(action: NavAction | null): void {
    if (action === this.navAction) return;
    this.navAction = action;
    this.deps.onNavAction?.(action);
  }

  // Windows browsers read a middle press as "start autoscroll", which would leave a drifting scroll
  // cursor fighting the orbit for the rest of the drag. Cancelling the compatibility mouse event is
  // what actually calls it off.
  private readonly onMouseDown = (e: MouseEvent): void => {
    if (e.button === MIDDLE_BUTTON) e.preventDefault();
  };

  private readonly onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  /**
   * `Shift` going down or coming up mid-drag switches orbit for pan, without waiting for a move.
   *
   * The move handler already re-reads the modifier, so the mapping was always live — but only from
   * the next mouse movement onwards, and a hand that presses `Shift` and *then* starts to drag would
   * spend that first instant showing the wrong pointer. No camera math runs here: a drag is deltas,
   * and there is no delta in a key press.
   */
  private readonly onShiftChange = (e: KeyboardEvent): void => {
    if (this.gesture === null || e.key !== 'Shift') return;
    this.gesture = modifiedGesture(e.shiftKey);
    this.reportNav(this.gesture);
  };

  constructor(element: HTMLElement, camera: CameraManager, deps: CameraControllerDeps = {}) {
    this.element = element;
    this.camera = camera;
    this.deps = deps;
    element.addEventListener('pointerdown', this.onPointerDown);
    element.addEventListener('pointermove', this.onPointerMove);
    element.addEventListener('pointerup', this.onPointerUp);
    element.addEventListener('pointercancel', this.onPointerUp);
    element.addEventListener('mousedown', this.onMouseDown);
    element.addEventListener('wheel', this.onWheel, { passive: false });
    element.addEventListener('contextmenu', this.onContextMenu);
    // On the window rather than the element: the drag has pointer capture, but keyboard focus can
    // sit anywhere, and a modifier pressed mid-drag has to be heard wherever it lands.
    window.addEventListener('keydown', this.onShiftChange);
    window.addEventListener('keyup', this.onShiftChange);
  }

  /**
   * The world point the wheel should zoom toward: where the cursor's ray meets the horizontal plane
   * through the current target.
   *
   * That plane, rather than the terrain itself, is the honest answer to "what am I pointing at" for
   * a pivot — it is where the view is already centred, it exists everywhere including out past the
   * edge of the site, and it costs one ray instead of a mesh raycast on every notch of the wheel.
   * A ray that finds no ground at all — the cursor up in the sky above the horizon — returns
   * nothing, and the zoom falls back to moving straight in on the target.
   */
  private zoomPivot(e: WheelEvent): THREE.Vector3 | null {
    const rect = this.element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    this.cursor.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.cursor, this.camera.activeCamera);
    this.zoomPlane.setFromNormalAndCoplanarPoint(UP, this.camera.orbit.target);
    return this.raycaster.ray.intersectPlane(this.zoomPlane, this.pivot);
  }

  private viewportHeight(): number {
    return this.element.clientHeight || this.element.getBoundingClientRect().height;
  }

  dispose(): void {
    this.clearZoomIdle();
    // The pointer outlives the controller, so it must not be left drawing a move that has no owner.
    this.reportNav(null);
    window.removeEventListener('keydown', this.onShiftChange);
    window.removeEventListener('keyup', this.onShiftChange);
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    this.element.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerup', this.onPointerUp);
    this.element.removeEventListener('pointercancel', this.onPointerUp);
    this.element.removeEventListener('mousedown', this.onMouseDown);
    this.element.removeEventListener('wheel', this.onWheel);
    this.element.removeEventListener('contextmenu', this.onContextMenu);
  }
}

/** The one button navigation claims. */
const MIDDLE_BUTTON = 1;

const UP = new THREE.Vector3(0, 1, 0);

/**
 * Every camera move navigation can be doing, in the order the doc block above lists them.
 *
 * A list rather than a bare union because the pointer glyphs are a table keyed on it (see
 * `ToolCursor`), and a table with a missing row should fail to compile rather than fail to draw.
 */
export const NAV_ACTIONS = ['orbit', 'pan', 'zoom-in', 'zoom-out'] as const;

/** One camera move. See {@link NAV_ACTIONS}. */
export type NavAction = (typeof NAV_ACTIONS)[number];

/** What a navigation *drag* is doing — the two moves a held button can be making. */
export type NavGesture = Extract<NavAction, 'orbit' | 'pan'>;

/** How long the zoom pointer stays up after the last notch of the wheel, in milliseconds. */
export const ZOOM_POINTER_LINGER_MS = 420;

/**
 * Which way a notch of the wheel takes the camera.
 *
 * Scrolling up reports a negative delta, which shrinks the dolly factor and so shortens the orbit
 * distance: up is in, the way every 3D tool reads it. Pure, so the sign can be pinned down without
 * a wheel to turn.
 */
export function zoomAction(pixels: number): NavAction {
  return pixels < 0 ? 'zoom-in' : 'zoom-out';
}

/**
 * Which navigation gesture a press starts, or `null` if navigation should keep its hands off it.
 * Pure, so the mapping can be asserted without a DOM.
 */
export function gestureFor(button: number, shift: boolean): NavGesture | null {
  if (button !== MIDDLE_BUTTON) return null;
  return modifiedGesture(shift);
}

/**
 * Which of the two a drag already under way is doing, given the modifier held at this instant. Its
 * own function because the drag re-asks on every move, and the answer must not drift from the one
 * {@link gestureFor} gave when the button went down.
 */
export function modifiedGesture(shift: boolean): NavGesture {
  return shift ? 'pan' : 'orbit';
}

/**
 * A wheel event's travel in CSS pixels, whatever unit the browser chose to report.
 *
 * Firefox quotes the wheel in lines and reports `3` where Chrome reports `100`, so a zoom rate tuned
 * against pixels and applied to the raw number is a zoom that barely moves in one browser and lurches
 * in the other. The line and page sizes are the conventional approximations; nothing exposes the
 * real ones.
 */
export function wheelPixels(e: Pick<WheelEvent, 'deltaY' | 'deltaMode'>): number {
  if (e.deltaMode === 1) return e.deltaY * 16;
  if (e.deltaMode === 2) return e.deltaY * 400;
  return e.deltaY;
}
